import api from "../api";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import "./UserGallery.css";

function UserGallery() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useContext(AuthContext);

  const [images, setImages] = useState([]);
  const [links, setLinks] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");

  /* ======================
     SUCCESS MESSAGE
  ====================== */
  useEffect(() => {
    if (location.state?.successMsg) {
      setSuccessMsg(location.state.successMsg);
      const timer = setTimeout(() => setSuccessMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  /* ======================
     FETCH DATA
  ====================== */
  useEffect(() => {
    fetchImages();
    fetchLinks();
  }, []);

  const fetchImages = async () => {
    try {
      const res = await api.get("/images");
      setImages(res.data);
    } catch (err) {
      console.error("Fetch images failed:", err);
    }
  };

  const fetchLinks = async () => {
    try {
      const res = await api.get("/links");
      setLinks(res.data);
    } catch (err) {
      console.error("Fetch links failed:", err);
    }
  };

  /* ======================
     COPY LINK
  ====================== */
  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setSuccessMsg("Link copied!");
    setTimeout(() => setSuccessMsg(""), 2000);
  };

  /* ======================
     DELETE LINK
  ====================== */
  const deleteLink = async (id) => {
    if (!window.confirm("Delete this download link permanently?")) return;

    try {
      await api.delete(`/links/${id}`);
      setLinks(prev => prev.filter(link => link._id !== id));
    } catch (err) {
      console.error("Delete link failed:", err);
      alert("Failed to delete link");
    }
  };

  /* ======================
     DELETE IMAGE SIZE
  ====================== */
  const deleteSize = async (imageId, url) => {
    if (!window.confirm("Delete this resized image?")) return;

    try {
      await api.delete("/images/delete-size", {
        data: { imageId, url }
      });

      setImages(prev =>
        prev
          .map(img =>
            img._id === imageId
              ? { ...img, sizes: img.sizes.filter(s => s.url !== url) }
              : img
          )
          .filter(img => img.sizes.length > 0)
      );
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete image size");
    }
  };

  /* ======================
     LOGOUT
  ====================== */
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    navigate("/");
  };

  return (
    <div className="gallery-page">
      {successMsg && <div className="success-msg">{successMsg}</div>}

      {/* HEADER */}
      <header className="gallery-header">
        <h1>Gallery</h1>

        <div style={{ display: "flex", gap: "10px" }}>
          {role === "admin" && (
            <button
              className="admin-btn"
              onClick={() => navigate("/admin/upload")}
            >
              Back
            </button>
          )}

          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      {/* CONTENT */}
      <main className="gallery-content">

        {/* ===== DOWNLOAD LINKS ===== */}
        {links.length > 0 && (
          <>
            <h3 className="section-title section-divider">
              Download Links
            </h3>

            <div className="links-list">
              {links.map(link => (
                <div key={link._id} className="link-card">
                  <span className="link-name">{link.originalName}</span>

                  <div className="link-actions">

                    {/* DOWNLOAD */}
                    <a
                      href={`${link.fileUrl}?download=1`}
                      download={link.originalName}
                      className="download-link"
                    >
                      Download
                    </a>

                    {/* COPY LINK */}
                    <button
                      className="copy-link-btn"
                      onClick={() => copyLink(link.fileUrl)}
                    >
                      Copy Link
                    </button>

                    {/* DELETE (ADMIN ONLY) */}
                    {role === "admin" && (
                      <button
                        className="delete-link-btn"
                        onClick={() => deleteLink(link._id)}
                      >
                        Delete
                      </button>
                    )}

                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ===== IMAGES ===== */}
        <h3 className="section-title">Download Images</h3>

        <div className="gallery-grid">
          {images.map(img => (
            <div className="image-card" key={img._id}>
              <h4 className="image-title">{img.originalName}</h4>

              <div className="thumb-container">
                {img.sizes.map((s, i) => (
                  <div className="thumb-box" key={i}>
                    <img
                      src={s.url}
                      alt={`${s.width}x${s.height}`}
                      onClick={() =>
                        navigate(`/view?url=${encodeURIComponent(s.url)}`)
                      }
                    />

                    <span>{s.width} � {s.height}</span>

                    {role === "admin" && (
                      <button
                        className="delete-btn"
                        onClick={() => deleteSize(img._id, s.url)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <p className="hint">Click any image to view full resolution</p>
            </div>
          ))}
        </div>

      </main>
    </div>
  );
}

export default UserGallery;